import type { SettlementStatus } from "@/features/clinic/types/billing";

import { useMockStore } from "../../store";

import {
  countsTowardValue,
  sumInvoiceAmounts,
} from "@/features/clinic/helpers/invoice";
import { SETTLEMENT_STATUS } from "@/features/clinic/types/billing";

type Settlement = { appointmentId: string | null; status: SettlementStatus };

const sumAmounts = (rows: { amount: string }[]) =>
  rows.reduce((total, row) => total + Number(row.amount), 0);

const clearedFor = <T extends Settlement>(rows: T[], appointmentId: string) =>
  rows.filter(
    (row) =>
      row.appointmentId === appointmentId &&
      row.status === SETTLEMENT_STATUS.CLEARED,
  );

/**
 * Mirrors the server: the value is read off the ledger, never written by a
 * client. Cleared receipts attached to the visit price it; failing those,
 * cleared advances; failing those, the invoices that still count. Called after
 * every write that can move a row on or off the appointment.
 */
export function deriveAppointmentValue(appointmentId: string) {
  const store = useMockStore.getState();
  const receipts = clearedFor(store.receipts, appointmentId);
  const advances = clearedFor(store.advances, appointmentId);
  const invoices = store.invoices.filter(
    (invoice) =>
      invoice.appointmentId === appointmentId && countsTowardValue(invoice),
  );

  const value =
    receipts.length > 0
      ? sumAmounts(receipts)
      : advances.length > 0
        ? sumAmounts(advances)
        : invoices.length > 0
          ? sumInvoiceAmounts(invoices)
          : null;

  store.updateAppointment(appointmentId, {
    value: value === null ? null : value.toFixed(2),
  });
}
